import {
  aiBlockProposalSchema,
  aiEvidenceReviewPayloadSchema,
  aiExplanationSchema,
  aiTrendAnalysisSchema,
  aiWeeklyReviewSchema,
  auditAIContext,
  coachAIResponseSchema,
  contextHash,
  type AIBlockProposal,
  type AIEvidenceReviewPayload,
  type AIExplanation,
  type AIPurpose,
  type AITrendAnalysis,
  type AIWeeklyReview,
} from '@cadencia/contracts';
import {
  aiFail,
  aiOk,
  estimateContextTokens,
  MAX_CONTEXT_TOKENS,
  CLAVES_DE_DATOS_DE_EVIDENCIA,
  proseFragmentsOf,
  validateAIProse,
  type AIResult,
  type BlockProposalContext,
  type EvidenceReviewContext,
  type CoachAIProvider,
  type ExplanationContext,
  type TrendContext,
  type WeeklyReviewContext,
} from '@cadencia/domain';
import type { SupabaseClient } from '@supabase/supabase-js';
import { logger } from '../../lib/logger.js';

/**
 * Implementacion de `CoachAIProvider` contra la Edge Function `coach-ai`
 * (docs/AI_ARCHITECTURE.md §4 y §5).
 *
 * Esta clase es el unico sitio de la aplicacion que sabe que existe una IA
 * remota. El dominio habla con la interfaz; las pantallas hablan con el
 * dominio. Si manana se quita la IA, se sustituye por `DisabledCoachAI` y no
 * cambia nada mas.
 *
 * TRES COSAS QUE NO HACE, A PROPOSITO
 *
 * 1. **No lanza nunca.** Toda ruta de fallo —red caida, funcion sin desplegar,
 *    respuesta fuera de contrato— sale como `AIResult` con `ok: false`. Un
 *    `throw` desde aqui obligaria a cada pantalla a envolver la llamada, y la
 *    primera que se olvidara se quedaria en blanco (trampa documentada en
 *    CLAUDE.md sobre `await` dentro de `useEffect`).
 *
 * 2. **No espera a la red para que la pantalla funcione.** Quien llama enseña
 *    primero su texto determinista y sustituye —o no— cuando esto conteste.
 *    La IA no bloquea ningun flujo.
 *
 * 3. **No decide.** Valida la FORMA de lo que llega y aplica las reglas de
 *    dominio sobre el texto (§8), pero no elige nada del plan: los tres
 *    propositos que atiende son de redaccion, y la decision que explican ya la
 *    tomo el motor determinista antes de que existiera la peticion.
 */

/**
 * Techo de espera en el cliente, POR PROPOSITO.
 *
 * Era uno solo de 45 s, y con eso `evidence_review` no podia funcionar NUNCA:
 * hace dos llamadas y la primera ejecuta busquedas y descargas. La primera
 * prueba real, el 22/09/2026, murio a los 45 s en el navegador con «no se pudo
 * revisar (unavailable)» —indistinguible de no tener red— y en el servidor no
 * quedo ni una fila, porque la funcion se queda sin cliente al que contestar.
 *
 * El techo de arriba no lo pone este fichero: una Edge Function de Supabase
 * devuelve 504 si no contesta en 150 s. Por eso el de la revision se queda en
 * 140: lo justo por debajo, para que quien corte sea la funcion —que sabe
 * decir por que— y no la pasarela.
 */
export const TIMEOUT_MS = 45_000;
export const TIMEOUT_POR_PROPOSITO: Partial<Record<AIPurpose, number>> = {
  evidence_review: 140_000,
  /*
   * Medido contra el proveedor el 24/09/2026: 51 s y 106 s por propuesta. Con
   * 45 s el cliente abandonaba, la función seguía, se pagaba y el bloque salía
   * de la plantilla. La función corta a los 115 s (`coach-ai/tiempos.ts`);
   * esto espera un poco más, para que el motivo lo dé ella.
   */
  block_proposal: 135_000,
};

/**
 * Lo que la pasarela de Supabase aguanta antes de devolver 504 («request idle
 * timeout», documentación oficial consultada el 22/09/2026). No es un ajuste
 * de este proyecto: es el techo dentro del que hay que caber.
 */
export const TECHO_DE_LA_PASARELA_MS = 150_000;

const ESQUEMAS = {
  explanation: aiExplanationSchema,
  weekly_review: aiWeeklyReviewSchema,
  trend_analysis: aiTrendAnalysisSchema,
  block_proposal: aiBlockProposalSchema,
  /*
   * El esquema del PAYLOAD, no el de la respuesta del modelo, y la diferencia
   * costó una revisión entera (22/09/2026).
   *
   * Zod **quita las claves que no conoce**. `aiEvidenceReviewSchema` es lo que
   * escribe el modelo y no incluye `retrievedUrls`, que lo añade la Edge
   * Function con las URL que sus herramientas descargaron de verdad. Validando
   * con él, el `parse` se llevaba por delante justo ese campo, y el paso
   * siguiente —que lo EXIGE para poder comprobar la procedencia de cada
   * ficha— rechazaba la respuesta entera por «no traía la forma esperada».
   *
   * Síntoma: una revisión que buscó, descargó, redactó cuatro fichas con su
   * DOI, se pagó (27,6 céntimos) y no guardó nada.
   */
  evidence_review: aiEvidenceReviewPayloadSchema,
} as const;

/**
 * Como se anota el veredicto del dominio.
 *
 * Entra por parametro y no se importa aqui a proposito: esta clase es un
 * adaptador de red y no debe saber que existe Dexie ni el outbox. Quien la
 * construye —`useCoachAI`— es quien tiene contexto de escritura.
 */
export type RegistrarVeredicto = (entrada: {
  decisionId: string;
  aiRequestId: string;
  payload: Record<string, unknown>;
  accepted: boolean;
  rejectionReason: string | null;
}) => Promise<unknown>;

/**
 * Propósitos cuyo veredicto lo pone el servicio que termina de validarlos, no
 * el filtro de prosa (ver `recordVerdict` en `CoachAIProvider`).
 */
const CON_VEREDICTO_DIFERIDO: ReadonlySet<AIPurpose> = new Set(['block_proposal', 'evidence_review']);

export class SupabaseCoachAI implements CoachAIProvider {
  /** Decisiones que pasaron la prosa y esperan el veredicto del dominio. */
  private readonly pendientes = new Map<string, { aiRequestId: string; payload: object }>();

  constructor(
    private readonly supabase: SupabaseClient,
    readonly enabled: boolean,
    private readonly registrarVeredicto?: RegistrarVeredicto,
  ) {}

  async explain(ctx: ExplanationContext): Promise<AIResult<AIExplanation>> {
    return this.pedir('explanation', ctx);
  }

  async reviewWeek(ctx: WeeklyReviewContext): Promise<AIResult<AIWeeklyReview>> {
    return this.pedir('weekly_review', ctx);
  }

  async analyzeTrend(ctx: TrendContext): Promise<AIResult<AITrendAnalysis>> {
    return this.pedir('trend_analysis', ctx);
  }


  async proposeBlock(ctx: BlockProposalContext): Promise<AIResult<AIBlockProposal>> {
    return this.pedir('block_proposal', ctx);
  }

  async reviewEvidence(ctx: EvidenceReviewContext): Promise<AIResult<AIEvidenceReviewPayload>> {
    return this.pedir('evidence_review', ctx);
  }


  /**
   * El camino comun. El orden importa y es este:
   *
   * interruptor → auditoria → tope de contexto → hash → red → contrato.
   *
   * Las tres primeras comprobaciones **no salen a red**: apagar la IA, o
   * mandarle sin querer un nombre, no debe costar ni una peticion.
   */
  private async pedir<P extends keyof typeof ESQUEMAS>(
    purpose: P,
    contexto: object,
  ): Promise<AIResult<(typeof ESQUEMAS)[P]['_output']>> {
    if (!this.enabled) return aiFail({ kind: 'disabled' });

    // Primera barrera. La que cuenta esta en la Edge Function, que es el punto
    // de salida real; esta ahorra la peticion y deja el aviso cerca de quien
    // construyo el contexto mal.
    const auditoria = auditAIContext(contexto);
    if (!auditoria.clean) {
      logger.warn('ai', 'CONTEXT_NOT_CLEAN', 'Contexto con datos identificativos, no se envia', {
        purpose,
        violations: auditoria.violations,
      });
      return aiFail({ kind: 'disabled', detail: auditoria.violations.join('; ').slice(0, 300) });
    }

    const tokens = estimateContextTokens(contexto);
    if (tokens > MAX_CONTEXT_TOKENS[purpose as AIPurpose]) {
      logger.warn('ai', 'CONTEXT_TOO_LARGE', 'Contexto por encima del tope, no se envia', {
        purpose,
        tokens,
        tope: MAX_CONTEXT_TOKENS[purpose as AIPurpose],
      });
      return aiFail({ kind: 'disabled', detail: `contexto de ~${tokens} tokens` });
    }

    let bruto: unknown;
    try {
      const hash = await contextHash(contexto);
      const { data, error } = await this.supabase.functions.invoke('coach-ai', {
        body: { purpose, context: contexto, contextHash: hash },
        // `invoke` no tiene timeout propio: sin esto una funcion colgada deja
        // la promesa viva hasta que el navegador se canse.
        signal: AbortSignal.timeout(TIMEOUT_POR_PROPOSITO[purpose as AIPurpose] ?? TIMEOUT_MS),
      });
      if (error) return aiFail({ kind: 'unavailable', detail: String(error.message).slice(0, 300) });
      bruto = data;
    } catch (error) {
      // Red, DNS, CORS, aborto por timeout. Para el dominio es lo mismo —no hay
      // IA, y hay camino determinista— pero para quien mira la pantalla NO: uno
      // se arregla con cobertura y el otro no se arregla esperando. Por eso el
      // motivo viaja con el resultado y se distingue el tiempo agotado.
      const agotado = error instanceof DOMException && error.name === 'TimeoutError';
      return aiFail({
        kind: 'unavailable',
        detail: agotado
          ? 'tardó demasiado y se canceló desde este dispositivo'
          : String(error).slice(0, 300),
      });
    }

    const respuesta = coachAIResponseSchema.safeParse(bruto);
    if (!respuesta.success) {
      // La funcion contesto algo que no es su propio contrato. Es un fallo de
      // despliegue, no del modelo, y merece registrarse aunque el usuario solo
      // vea el texto determinista.
      logger.warn('ai', 'RESPONSE_OFF_CONTRACT', 'coach-ai contesto fuera de contrato', {
        purpose,
        detalle: respuesta.error.message.slice(0, 300),
      });
      return aiFail({ kind: 'invalid_output', details: respuesta.error.message.slice(0, 500) });
    }
    if (!respuesta.data.ok) return aiFail(respuesta.data.error);

    // Segunda validacion del contenido. La Edge Function ya lo comprobo contra
    // el mismo esquema; se repite aqui porque una version vieja desplegada
    // seguiria contestando 200 con la forma de ayer.
    const payload = ESQUEMAS[purpose].safeParse(respuesta.data.payload);
    if (!payload.success) {
      // Tambien se anota. La Edge Function ya inserto la fila al pasar SU
      // esquema, y la respuesta ya se pago: salir sin anotar dejaba `accepted`
      // en nulo para siempre, contra lo que dice el comentario de mas abajo.
      // Pasa cuando los dos esquemas no coinciden, que es un fallo de
      // despliegue y justo el que conviene poder contar despues.
      this.anotar(respuesta.data.aiDecisionId, respuesta.data.aiRequestId, respuesta.data.payload, {
        accepted: false,
        motivo: `esquema del cliente: ${payload.error.message}`,
      });
      return aiFail({ kind: 'invalid_output', details: payload.error.message.slice(0, 500) });
    }

    // Tercer paso del §8: las REGLAS DE DOMINIO. El esquema comprueba que hay
    // un titular de entre 10 y 160 caracteres; esto comprueba que dentro no
    // hay HTML ni un enlace inventado. Va aqui, en el unico punto por el que
    // pasan los tres propositos, y no en cada pantalla: una comprobacion que
    // hay que acordarse de llamar es una comprobacion que un dia no se llama.
    const veredicto = validateAIProse(
      proseFragmentsOf(
        payload.data,
        // En una revisión de literatura la URL de una ficha ES el dato, y su
        // procedencia la comprueba `validateRetrieved`, que es más estricto.
        purpose === 'evidence_review' ? CLAVES_DE_DATOS_DE_EVIDENCIA : undefined,
      ),
    );

    // El veredicto se ANOTA, se acepte o se rechace. Un rechazo registrado es
    // tan util como una aceptacion: es la prueba de que el dominio manda sobre
    // el modelo, y sin el la afirmacion no tiene detras ni una fila.
    //
    // No se espera: es contabilidad, no la decision. Si falla, la respuesta
    // sigue su curso y solo se pierde el apunte.
    // Se anota lo que LLEGO, no lo validado: el `parse` quita las claves que su
    // esquema no conoce, y esta fila es la que la Edge Function sirve en el
    // siguiente acierto de cache. Reescribirla recortada la estropea para eso.
    //
    // Con UNA excepción desde el 23/09/2026: en los propósitos cuya validación
    // sigue fuera de aquí (`CON_VEREDICTO_DIFERIDO`), pasar la prosa no es
    // aceptar. Se deja pendiente, y el servicio que valida volumen, estructura
    // o procedencia pone el veredicto final con `recordVerdict`. Anotar aquí
    // `true` dejaba como aceptadas propuestas que después se rechazaban.
    if (!veredicto.accepted || !CON_VEREDICTO_DIFERIDO.has(purpose)) {
      this.anotar(respuesta.data.aiDecisionId, respuesta.data.aiRequestId, respuesta.data.payload, {
        accepted: veredicto.accepted,
        motivo: veredicto.accepted ? null : veredicto.violations.join('; '),
      });
    } else {
      this.pendientes.set(respuesta.data.aiDecisionId, {
        aiRequestId: respuesta.data.aiRequestId,
        payload: respuesta.data.payload,
      });
    }

    if (!veredicto.accepted) {
      logger.warn('ai', 'PROSE_REJECTED', 'Redaccion rechazada por las reglas de dominio', {
        purpose,
        violations: veredicto.violations,
      });
      return aiFail({ kind: 'rejected_by_rules', violations: veredicto.violations });
    }

    return aiOk(payload.data, respuesta.data.aiDecisionId, respuesta.data.promptVersion);
  }

  /** El veredicto final de una propuesta que se quedó pendiente. */
  recordVerdict(decisionId: string, verdict: { accepted: boolean; reason: string | null }): void {
    const pendiente = this.pendientes.get(decisionId);
    if (pendiente === undefined) return;
    this.pendientes.delete(decisionId);
    this.anotar(decisionId, pendiente.aiRequestId, pendiente.payload, {
      accepted: verdict.accepted,
      motivo: verdict.reason,
    });
  }

  private anotar(
    decisionId: string,
    aiRequestId: string,
    payload: object,
    veredicto: { accepted: boolean; motivo: string | null },
  ): void {
    if (this.registrarVeredicto === undefined) return;
    void this.registrarVeredicto({
      decisionId,
      aiRequestId,
      payload: payload as Record<string, unknown>,
      accepted: veredicto.accepted,
      rejectionReason: veredicto.motivo,
    }).catch((error: unknown) => {
      logger.warn('ai', 'VERDICT_NOT_RECORDED', 'No se pudo anotar el veredicto', {
        decisionId,
        detalle: String(error).slice(0, 200),
      });
    });
  }
}
